import { useEffect, useRef, useState, useCallback } from "react";
import { SESSION_IDLE_CONFIG, SESSION_IDLE_EVENTS } from "@/config/session-idle";

type Phase = "active" | "warning";

interface UseIdleSessionArgs {
  enabled: boolean;
  onLogout: () => void;
}

interface UseIdleSessionState {
  phase: Phase;
  /** Ms remaining until auto-logout when phase === "warning". */
  remainingMs: number;
  /** Reset idle timer and dismiss the warning if shown. */
  stayActive: () => void;
}

/**
 * Centralized idle-session manager.
 *
 * - Single set of listeners for the whole app.
 * - Cross-tab sync via BroadcastChannel (localStorage fallback).
 * - Emits a `warning` phase at `warnAfterMs`, invokes `onLogout` at `logoutAfterMs`.
 * - Ignores programmatic/background events — only real user gestures reset.
 */
export function useIdleSession({ enabled, onLogout }: UseIdleSessionArgs): UseIdleSessionState {
  const [phase, setPhase] = useState<Phase>("active");
  const [remainingMs, setRemainingMs] = useState(
    SESSION_IDLE_CONFIG.logoutAfterMs - SESSION_IDLE_CONFIG.warnAfterMs,
  );

  const lastActivityRef = useRef<number>(Date.now());
  const lastBroadcastRef = useRef<number>(0);
  const warnTimerRef = useRef<number | null>(null);
  const logoutTimerRef = useRef<number | null>(null);
  const tickTimerRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const firedLogoutRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) window.clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) window.clearTimeout(logoutTimerRef.current);
    if (tickTimerRef.current) window.clearInterval(tickTimerRef.current);
    warnTimerRef.current = null;
    logoutTimerRef.current = null;
    tickTimerRef.current = null;
  }, []);

  const scheduleFrom = useCallback(
    (baseTs: number) => {
      clearTimers();
      const now = Date.now();
      const elapsed = now - baseTs;
      const untilWarn = SESSION_IDLE_CONFIG.warnAfterMs - elapsed;
      const untilLogout = SESSION_IDLE_CONFIG.logoutAfterMs - elapsed;

      if (untilLogout <= 0) {
        if (!firedLogoutRef.current) {
          firedLogoutRef.current = true;
          onLogout();
        }
        return;
      }

      if (untilWarn > 0) {
        setPhase("active");
        warnTimerRef.current = window.setTimeout(() => {
          setPhase("warning");
          setRemainingMs(SESSION_IDLE_CONFIG.logoutAfterMs - SESSION_IDLE_CONFIG.warnAfterMs);
          tickTimerRef.current = window.setInterval(() => {
            const left = SESSION_IDLE_CONFIG.logoutAfterMs - (Date.now() - lastActivityRef.current);
            setRemainingMs(Math.max(0, left));
          }, 1000);
        }, untilWarn);
      } else {
        setPhase("warning");
        setRemainingMs(Math.max(0, untilLogout));
        tickTimerRef.current = window.setInterval(() => {
          const left = SESSION_IDLE_CONFIG.logoutAfterMs - (Date.now() - lastActivityRef.current);
          setRemainingMs(Math.max(0, left));
        }, 1000);
      }

      logoutTimerRef.current = window.setTimeout(() => {
        if (!firedLogoutRef.current) {
          firedLogoutRef.current = true;
          onLogout();
        }
      }, untilLogout);
    },
    [clearTimers, onLogout],
  );

  const broadcastActivity = useCallback((ts: number) => {
    try {
      channelRef.current?.postMessage({ type: "activity", ts });
    } catch { /* ignore */ }
    try {
      localStorage.setItem(SESSION_IDLE_CONFIG.storageKey, String(ts));
    } catch { /* ignore */ }
  }, []);

  const registerActivity = useCallback(
    (fromRemote = false) => {
      const now = Date.now();
      // Throttle to avoid resetting timers on every mousemove tick.
      if (now - lastBroadcastRef.current < SESSION_IDLE_CONFIG.activityThrottleMs) {
        lastActivityRef.current = now;
        return;
      }
      lastBroadcastRef.current = now;
      lastActivityRef.current = now;
      scheduleFrom(now);
      if (!fromRemote) broadcastActivity(now);
    },
    [scheduleFrom, broadcastActivity],
  );

  const stayActive = useCallback(() => {
    firedLogoutRef.current = false;
    lastBroadcastRef.current = 0; // force through throttle
    registerActivity(false);
  }, [registerActivity]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setPhase("active");
      return;
    }

    firedLogoutRef.current = false;
    lastActivityRef.current = Date.now();
    scheduleFrom(lastActivityRef.current);

    // Local user-activity listeners.
    const onLocal = () => registerActivity(false);
    SESSION_IDLE_EVENTS.forEach((ev) =>
      window.addEventListener(ev, onLocal, { passive: true } as AddEventListenerOptions),
    );

    // Cross-tab: BroadcastChannel primary, storage event fallback.
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(SESSION_IDLE_CONFIG.channelName);
      bc.onmessage = (e) => {
        if (e?.data?.type === "activity") registerActivity(true);
        if (e?.data?.type === "logout" && !firedLogoutRef.current) {
          firedLogoutRef.current = true;
          onLogout();
        }
      };
      channelRef.current = bc;
    } catch { /* older browsers */ }

    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_IDLE_CONFIG.storageKey && e.newValue) registerActivity(true);
    };
    window.addEventListener("storage", onStorage);

    return () => {
      SESSION_IDLE_EVENTS.forEach((ev) => window.removeEventListener(ev, onLocal));
      window.removeEventListener("storage", onStorage);
      clearTimers();
      try { bc?.close(); } catch { /* ignore */ }
      channelRef.current = null;
    };
  }, [enabled, registerActivity, scheduleFrom, clearTimers, onLogout]);

  return { phase, remainingMs, stayActive };
}

/** Broadcast a logout so sibling tabs sign out too. */
export function broadcastIdleLogout() {
  try {
    const bc = new BroadcastChannel(SESSION_IDLE_CONFIG.channelName);
    bc.postMessage({ type: "logout" });
    bc.close();
  } catch { /* ignore */ }
}
